﻿import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  TextInput,
  Image,
  Platform,
  ActivityIndicator,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as ImagePicker from 'expo-image-picker';
import {
  Ionicons,
  Feather,
  MaterialIcons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';

const EditLearningProfile = ({ route }) => {
  const navigation = useNavigation();
  const { userData: initialUserData } = route.params || {};

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    bio: '',
    location: '',
    educationLevel: '',
    university: '',
    major: '',
    graduationYear: '',
    dateOfBirth: new Date(),
    gender: '',
    interests: [],
  });

  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempInterests, setTempInterests] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Color scheme
  const colors = {
    primary: '#4F46E5',
    secondary: '#10B981',
    accent: '#F59E0B',
    danger: '#EF4444',
    warning: '#F97316',
    info: '#0EA5E9',
    purple: '#8B5CF6',
    dark: '#1F2937',
    light: '#F9FAFB',
    white: '#FFFFFF',
    gray: '#6B7280',
    lightGray: '#E5E7EB',
  };

  // Education levels
  const educationLevels = [
    'High School',
    'Associate Degree',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'PhD',
    'Professional Certification',
    'Other',
  ];

  // Gender options
  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];

  // Common interests for learning
  const commonInterests = [
    'Web Development',
    'Mobile Development',
    'Data Science',
    'AI/ML',
    'Cloud Computing',
    'Cybersecurity',
    'UI/UX Design',
    'DevOps',
    'Blockchain',
    'Game Development',
    'IoT',
    'Robotics',
  ];

  useEffect(() => {
    loadUserData();
    loadProfileImage();
    requestPermissions();
  }, []);

  const requestPermissions = async () => {
    await ImagePicker.requestMediaLibraryPermissionsAsync();
    await ImagePicker.requestCameraPermissionsAsync();
  };

  const loadUserData = async () => {
    try {
      // Load from passed data or AsyncStorage
      if (initialUserData) {
        setFormData(prev => ({
          ...prev,
          fullName: initialUserData.fullName || initialUserData.name || '',
          email: initialUserData.email || '',
          phone: initialUserData.phone || '',
          bio: initialUserData.bio || '',
          location: initialUserData.location || '',
          educationLevel: initialUserData.educationLevel || initialUserData.education || '',
          university: initialUserData.university || '',
          major: initialUserData.major || '',
          graduationYear: initialUserData.graduationYear || '',
          dateOfBirth: initialUserData.dateOfBirth ? new Date(initialUserData.dateOfBirth) : new Date(),
          gender: initialUserData.gender || '',
          interests: initialUserData.interests || [],
        }));
      }

      // Also try to load from AsyncStorage
      const storedUser = await AsyncStorage.getItem('learning_user_data');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setFormData(prev => ({
          ...prev,
          ...parsedUser,
          dateOfBirth: parsedUser.dateOfBirth ? new Date(parsedUser.dateOfBirth) : new Date(),
        }));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadProfileImage = async () => {
    try {
      const savedImage = await AsyncStorage.getItem('learning_profile_image');
      if (savedImage) {
        setProfileImage(savedImage);
      }
    } catch (error) {
      console.error('Error loading profile image:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDateChange = (event, selectedDate) => {
    setShowDatePicker(Platform.OS === 'ios');
    if (selectedDate) {
      handleInputChange('dateOfBirth', selectedDate);
    }
  };

  const handleInterestAdd = () => {
    if (tempInterests.trim()) {
      const newInterest = tempInterests.trim();
      if (!formData.interests.includes(newInterest) && formData.interests.length < 8) {
        setFormData(prev => ({
          ...prev,
          interests: [...prev.interests, newInterest],
        }));
        setTempInterests('');
      }
    }
  };

  const handleInterestRemove = (interestToRemove) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.filter(interest => interest !== interestToRemove),
    }));
  };

  const handleInterestSelect = (interest) => {
    if (formData.interests.includes(interest)) {
      handleInterestRemove(interest);
    } else if (formData.interests.length < 8) {
      setFormData(prev => ({
        ...prev,
        interests: [...prev.interests, interest],
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\D/g, ''))) {
      errors.phone = 'Phone number is invalid';
    }

    if (formData.bio && formData.bio.length > 500) {
      errors.bio = 'Bio cannot exceed 500 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      Alert.alert('Validation Error', 'Please check the highlighted fields');
      return;
    }

    setSaving(true);
    try {
      // Prepare data for saving
      const userDataToSave = {
        ...formData,
        updatedAt: new Date().toISOString(),
      };

      // Save to AsyncStorage
      await AsyncStorage.setItem('learning_user_data', JSON.stringify(userDataToSave));

      // In a real app, you would also send to your backend API
      // await saveToBackend(userDataToSave);

      Alert.alert(
        'Success',
        'Profile updated successfully!',
        [
          {
            text: 'OK',
            onPress: () => navigation.goBack(),
          },
        ]
      );
    } catch (error) {
      console.error('Error saving profile:', error);
      Alert.alert('Error', 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeProfilePicture = () => {
    Alert.alert(
      'Update Profile Picture',
      'Choose an option',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Take Photo', onPress: openCamera },
        { text: 'Choose from Gallery', onPress: openGallery },
        ...(profileImage ? [{ text: 'Remove Photo', onPress: removeProfilePicture, style: 'destructive' }] : []),
      ]
    );
  };

  const openCamera = async () => {
    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        await saveProfileImage(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Error taking photo:', error);
      Alert.alert('Error', 'Failed to take photo. Please try again.');
    }
  };

  const openGallery = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        await saveProfileImage(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    }
  };

  const saveProfileImage = async (imageUri) => {
    try {
      await AsyncStorage.setItem('learning_profile_image', imageUri);
      setProfileImage(imageUri);
      Alert.alert('Success', 'Profile picture updated');
    } catch (error) {
      console.error('Error saving profile image:', error);
      Alert.alert('Error', 'Failed to save profile picture');
    }
  };

  const removeProfilePicture = async () => {
    try {
      await AsyncStorage.removeItem('learning_profile_image');
      setProfileImage(null);
      Alert.alert('Success', 'Profile picture removed');
    } catch (error) {
      console.error('Error removing profile image:', error);
      Alert.alert('Error', 'Failed to remove profile picture');
    }
  };

  const handleDiscardChanges = () => {
    Alert.alert(
      'Discard Changes',
      'Are you sure you want to discard all changes?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => navigation.goBack(),
        },
      ]
    );
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderInputField = (label, field, placeholder, multiline = false, keyboardType = 'default') => (
    <View style={styles.inputContainer}>
      <Text style={styles.inputLabel}>{label}</Text>
      <View style={[
        styles.inputWrapper,
        validationErrors[field] && styles.inputError,
        multiline && styles.multilineWrapper,
      ]}>
        <TextInput
          style={[
            styles.textInput,
            multiline && styles.multilineInput,
          ]}
          value={formData[field]}
          onChangeText={(text) => handleInputChange(field, text)}
          placeholder={placeholder}
          placeholderTextColor="#9CA3AF"
          multiline={multiline}
          numberOfLines={multiline ? 4 : 1}
          keyboardType={keyboardType}
        />
        {multiline && (
          <Text style={styles.charCounter}>
            {formData.bio?.length || 0}/500
          </Text>
        )}
      </View>
      {validationErrors[field] && (
        <Text style={styles.errorText}>{validationErrors[field]}</Text>
      )}
    </View>
  );

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={handleDiscardChanges} style={styles.backButton}>
              <Ionicons name="arrow-back" size={24} color={colors.dark} />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>Edit Profile</Text>
            <TouchableOpacity 
              onPress={handleSave} 
              style={styles.saveButton}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text style={styles.saveButtonText}>Save</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>

        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardView}
        >
          <ScrollView 
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
          >
            {/* Profile Picture Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Profile Picture</Text>
              <View style={styles.profileImageSection}>
                <TouchableOpacity onPress={handleChangeProfilePicture} style={styles.profileImageContainer}>
                  {profileImage ? (
                    <Image source={{ uri: profileImage || undefined }} style={styles.profileImage} />
                  ) : (
                    <View style={styles.defaultProfileImage}>
                      <Ionicons name="person" size={50} color={colors.gray} />
                    </View>
                  )}
                  <View style={styles.cameraIconContainer}>
                    <Feather name="camera" size={16} color={colors.white} />
                  </View>
                </TouchableOpacity>
                <Text style={styles.profileImageHint}>
                  Tap to change your profile picture
                </Text>
              </View>
            </View>

            {/* Personal Information Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Personal Information</Text>
              
              {renderInputField('Full Name *', 'fullName', 'Enter your full name')}
              {renderInputField('Email *', 'email', 'Enter your email', false, 'email-address')}
              {renderInputField('Phone Number', 'phone', 'Enter your phone number', false, 'phone-pad')}
              
              {/* Date of Birth */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Date of Birth</Text>
                <TouchableOpacity 
                  style={styles.datePickerButton}
                  onPress={() => setShowDatePicker(true)}
                >
                  <Text style={styles.dateText}>
                    {formatDate(formData.dateOfBirth)}
                  </Text>
                  <Feather name="calendar" size={20} color={colors.gray} />
                </TouchableOpacity>
                {showDatePicker && (
                  <DateTimePicker
                    value={formData.dateOfBirth}
                    mode="date"
                    display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                    onChange={handleDateChange}
                    maximumDate={new Date()}
                  />
                )}
              </View>

              {/* Gender */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Gender</Text>
                <View style={styles.genderOptions}>
                  {genderOptions.map((gender) => (
                    <TouchableOpacity
                      key={gender}
                      style={[
                        styles.genderOption,
                        formData.gender === gender && styles.genderOptionSelected,
                      ]}
                      onPress={() => handleInputChange('gender', gender)}
                    >
                      <Text style={[
                        styles.genderText,
                        formData.gender === gender && styles.genderTextSelected,
                      ]}>
                        {gender}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Location */}
              {renderInputField('Location', 'location', 'Enter your city/country')}
              
              {/* Bio */}
              {renderInputField('Bio', 'bio', 'Tell us about yourself...', true)}
            </View>

            {/* Education Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Education</Text>
              
              {/* Education Level */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Education Level</Text>
                <View style={styles.selectWrapper}>
                  <Text style={styles.selectText}>
                    {formData.educationLevel || 'Select education level'}
                  </Text>
                  <Feather name="chevron-down" size={20} color={colors.gray} />
                </View>
                <View style={styles.dropdownOptions}>
                  {educationLevels.map((level) => (
                    <TouchableOpacity
                      key={level}
                      style={[
                        styles.dropdownOption,
                        formData.educationLevel === level && styles.dropdownOptionSelected,
                      ]}
                      onPress={() => handleInputChange('educationLevel', level)}
                    >
                      <Text style={[
                        styles.dropdownOptionText,
                        formData.educationLevel === level && styles.dropdownOptionTextSelected,
                      ]}>
                        {level}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {renderInputField('University', 'university', 'Enter your university')}
              {renderInputField('Major/Field of Study', 'major', 'Enter your major')}
              {renderInputField('Graduation Year', 'graduationYear', 'YYYY', false, 'number-pad')}
            </View>

            {/* Interests Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Learning Interests</Text>
              <Text style={styles.sectionSubtitle}>
                Select up to 8 interests to get personalized recommendations
              </Text>

              {/* Selected Interests */}
              {formData.interests.length > 0 && (
                <View style={styles.selectedInterests}>
                  {formData.interests.map((interest, index) => (
                    <View key={index} style={styles.interestTag}>
                      <Text style={styles.interestTagText}>{interest}</Text>
                      <TouchableOpacity
                        onPress={() => handleInterestRemove(interest)}
                        style={styles.removeInterestButton}
                      >
                        <Feather name="x" size={14} color={colors.white} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}

              {/* Add Interest Input */}
              <View style={styles.addInterestContainer}>
                <TextInput
                  style={styles.addInterestInput}
                  value={tempInterests}
                  onChangeText={setTempInterests}
                  placeholder="Add a custom interest..."
                  placeholderTextColor="#9CA3AF"
                  onSubmitEditing={handleInterestAdd}
                />
                <TouchableOpacity
                  style={styles.addInterestButton}
                  onPress={handleInterestAdd}
                  disabled={!tempInterests.trim() || formData.interests.length >= 8}
                >
                  <Feather name="plus" size={20} color={colors.white} />
                </TouchableOpacity>
              </View>

              {/* Common Interests */}
              <Text style={styles.commonInterestsTitle}>Common Interests</Text>
              <View style={styles.commonInterests}>
                {commonInterests.map((interest, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.commonInterestButton,
                      formData.interests.includes(interest) && styles.commonInterestButtonSelected,
                    ]}
                    onPress={() => handleInterestSelect(interest)}
                    disabled={formData.interests.length >= 8 && !formData.interests.includes(interest)}
                  >
                    <Text style={[
                      styles.commonInterestText,
                      formData.interests.includes(interest) && styles.commonInterestTextSelected,
                    ]}>
                      {interest}
                    </Text>
                    {formData.interests.includes(interest) && (
                      <Feather name="check" size={16} color={colors.white} style={styles.checkIcon} />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Action Buttons */}
            <View style={styles.actionButtons}>
              <TouchableOpacity
                style={[styles.actionButton, styles.cancelButton]}
                onPress={handleDiscardChanges}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.actionButton, styles.saveActionButton]}
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <>
                    <Feather name="save" size={18} color={colors.white} />
                    <Text style={styles.saveActionButtonText}>Save Changes</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  keyboardView: {
    flex: 1,
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  saveButton: {
    backgroundColor: '#4F46E5',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    minWidth: 60,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
  scrollContent: {
    paddingBottom: 30,
  },
  section: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    marginTop: 16,
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 16,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 16,
  },
  profileImageSection: {
    alignItems: 'center',
  },
  profileImageContainer: {
    position: 'relative',
    marginBottom: 12,
  },
  profileImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  defaultProfileImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cameraIconContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: '#4F46E5',
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: '#FFFFFF',
  },
  profileImageHint: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  inputContainer: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  inputWrapper: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  textInput: {
    padding: 12,
    fontSize: 16,
    color: '#1F2937',
  },
  multilineWrapper: {
    minHeight: 100,
  },
  multilineInput: {
    height: 100,
    textAlignVertical: 'top',
  },
  charCounter: {
    textAlign: 'right',
    fontSize: 12,
    color: '#9CA3AF',
    paddingHorizontal: 12,
    paddingBottom: 8,
  },
  errorText: {
    fontSize: 12,
    color: '#EF4444',
    marginTop: 4,
  },
  datePickerButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#FFFFFF',
  },
  dateText: {
    fontSize: 16,
    color: '#1F2937',
  },
  genderOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  genderOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
  },
  genderOptionSelected: {
    backgroundColor: '#4F46E5',
    borderColor: '#4F46E5',
  },
  genderText: {
    fontSize: 14,
    color: '#374151',
  },
  genderTextSelected: {
    color: '#FFFFFF',
    fontWeight: '500',
  },
  selectWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#FFFFFF',
  },
  selectText: {
    fontSize: 16,
    color: '#1F2937',
  },
  dropdownOptions: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    maxHeight: 200,
  },
  dropdownOption: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  dropdownOptionSelected: {
    backgroundColor: '#EEF2FF',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: '#374151',
  },
  dropdownOptionTextSelected: {
    color: '#4F46E5',
    fontWeight: '500',
  },
  selectedInterests: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  interestTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  interestTagText: {
    fontSize: 14,
    color: '#4F46E5',
    marginRight: 6,
  },
  removeInterestButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#4F46E5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addInterestContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  addInterestInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#FFFFFF',
    marginRight: 8,
  },
  addInterestButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: '#4F46E5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  commonInterestsTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 12,
  },
  commonInterests: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  commonInterestButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
  },
  commonInterestButtonSelected: {
    backgroundColor: '#4F46E5',
    borderColor: '#4F46E5',
  },
  commonInterestText: {
    fontSize: 14,
    color: '#374151',
  },
  commonInterestTextSelected: {
    color: '#FFFFFF',
    marginRight: 4,
  },
  checkIcon: {
    marginLeft: 2,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: 20,
    marginTop: 24,
    marginBottom: 30,
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
  saveActionButton: {
    backgroundColor: '#4F46E5',
  },
  saveActionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
});

export default EditLearningProfile;